import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { getBookings, Booking, addBooking } from "../api/admin";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  List,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  Filter,
  Clock,
  User,
  Phone,
  Mail,
  Users,
  Calendar as CalendarIcon,
  MessageSquare,
  X,
  Plus,
  Download,
  ArrowUp,
  ArrowDown,
  Eye,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Enhanced Booking type with notes
interface EnhancedBooking extends Booking {
  notes?: string;
}

// Column sorting type
type SortColumn = "name" | "date" | "time" | "guests" | "email" | null;
type SortDirection = "asc" | "desc";

export default function AdminDashboard() {
  const [bookings, setBookings] = useState<EnhancedBooking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<EnhancedBooking[]>(
    []
  );
  const [displayedBookings, setDisplayedBookings] = useState<EnhancedBooking[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // View state: 'table' or 'calendar'
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");

  // State for calendar navigation
  const [currentDate, setCurrentDate] = useState(new Date());

  // Selected booking for detail view
  const [selectedBooking, setSelectedBooking] =
    useState<EnhancedBooking | null>(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState<string>("upcoming");
  const [showFilters, setShowFilters] = useState(false);

  // Add booking modal state
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);

  // Add sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // New booking form state
  const [newBooking, setNewBooking] = useState<
    Omit<Booking, "id" | "createdAt">
  >({
    name: "",
    email: "",
    phone: "",
    date: new Date().toISOString().split("T")[0],
    time: "18:00",
    guests: "2",
    specialRequests: "",
    newsletter: false,
  });

  // Form validation state
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // State for saving notes
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Mobile panel state
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);

  // Function to check if a date is in the past
  const isDateInPast = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day

    const bookingDate = new Date(dateString);
    bookingDate.setHours(0, 0, 0, 0); // Reset time to start of day

    return bookingDate < today;
  };

  // Function to filter and sort bookings
  const filterAndSortBookings = (bookingsData: Booking[]) => {
    // Filter out past bookings
    const futureBookings = bookingsData.filter(
      (booking) => !isDateInPast(booking.date)
    );

    // Sort by date (ascending order - upcoming first)
    return futureBookings.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      // If dates are the same, sort by time
      if (dateA.getTime() === dateB.getTime()) {
        return a.time.localeCompare(b.time);
      }

      return dateA.getTime() - dateB.getTime();
    });
  };

  // Fixed authentication check to prevent flickering
  useEffect(() => {
    const authCheck = () => {
      // Only redirect when authentication state is confirmed (not null) and false
      if (isAuthenticated === false) {
        navigate("/admin");
      }
    };

    // Add a small delay to ensure auth state is properly loaded from localStorage
    const timeoutId = setTimeout(authCheck, 50);
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, navigate]);

  // Fetch bookings only when authenticated
  useEffect(() => {
    // Only fetch when authentication is confirmed
    if (isAuthenticated === true) {
      const fetchBookings = async () => {
        try {
          setIsLoading(true);
          setError(null);
          console.log("Fetching bookings for dashboard...");
          const data = await getBookings();
          console.log("Fetched bookings:", data);
          setBookings(data || []);

          // Filter and sort bookings
          const filteredData = filterAndSortBookings(data || []);
          setFilteredBookings(filteredData);

          console.log(
            `Filtered out ${(data || []).length - filteredData.length} past bookings`
          );
        } catch (err) {
          console.error("Error loading bookings:", err);
          setError(
            err instanceof Error ? err.message : "Failed to load bookings"
          );

          // If unauthorized, redirect to login
          if (
            err instanceof Error &&
            err.message.includes("session has expired")
          ) {
            navigate("/admin");
          }
        } finally {
          setIsLoading(false);
        }
      };

      fetchBookings();
    }
  }, [isAuthenticated, navigate]);

  // Fix the xs:hidden issue by properly checking viewport width
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show consistent loading state
  if (isAuthenticated === null || isAuthenticated === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <svg
            className="animate-spin h-10 w-10 text-primary mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-600 dark:text-gray-300">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setError(null); // Clear any previous errors

    try {
      console.log("Refreshing bookings data...");
      const data = await getBookings();

      // Check if the data is valid
      if (!data || !Array.isArray(data)) {
        console.error("Invalid data returned:", data);
        throw new Error("Invalid data returned from server");
      }

      console.log("Successfully fetched bookings:", data.length);

      // Log a sample booking for debugging
      if (data.length > 0) {
        console.log("Sample booking:", data[0]);
      }

      // Update the state with the new data
      setBookings(data);

      // Filter and sort bookings
      const filteredData = filterAndSortBookings(data);
      setFilteredBookings(filteredData);

      toast.success(
        `Successfully loaded ${filteredData.length} upcoming bookings`
      );
    } catch (err) {
      console.error("Error refreshing bookings:", err);
      setError(
        err instanceof Error ? err.message : "Failed to refresh bookings"
      );
      toast.error(
        err instanceof Error ? err.message : "Failed to refresh bookings"
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin");
  };

  // Format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Calendar navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  // Calendar rendering functions
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  // Format dates for calendar view matching
  const formatDateForComparison = (dateStr: string) => {
    try {
      const dateComponents = dateStr.split("-");
      if (dateComponents.length === 3) {
        const [year, month, day] = dateComponents;
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }
      return dateStr;
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateStr;
    }
  };

  // Group bookings by date with proper formatting
  const bookingsByDate = filteredBookings.reduce(
    (acc: Record<string, Booking[]>, booking) => {
      // Ensure date is properly formatted for comparison
      const formattedDate = formatDateForComparison(booking.date);

      if (!acc[formattedDate]) {
        acc[formattedDate] = [];
      }
      acc[formattedDate].push(booking);
      return acc;
    },
    {}
  );

  // Render calendar view
  const renderCalendarView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);

    // Generate calendar days
    const days: JSX.Element[] = [];

    // Add empty cells for the days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className="h-24 border border-gray-200 dark:border-gray-700"
        ></div>
      );
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      // Format date consistently for comparison with bookings data
      const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      console.log(`Checking for bookings on ${date}`);

      const dayBookings = bookingsByDate[date] || [];
      const hasBookings = dayBookings.length > 0;

      if (hasBookings) {
        console.log(`Found ${dayBookings.length} bookings for ${date}`);
      }

      days.push(
        <div
          key={day}
          className={`min-h-16 md:min-h-24 p-1 md:p-2 border border-gray-200 dark:border-gray-700 transition-colors ${hasBookings ? "bg-primary/5 dark:bg-primary/10" : ""}`}
        >
          <div className="font-semibold text-right mb-1 text-xs md:text-sm">
            {day}
          </div>
          {hasBookings ? (
            <div className="space-y-1">
              {dayBookings.slice(0, 3).map((booking, idx) => (
                <div
                  key={idx}
                  className="text-xs p-1 bg-white dark:bg-gray-700 rounded shadow truncate"
                >
                  <span className="font-medium">{booking.time}</span> -{" "}
                  {windowWidth > 480
                    ? booking.name
                    : booking.name.split(" ")[0]}{" "}
                  ({booking.guests})
                </div>
              ))}
              {dayBookings.length > 3 && (
                <div className="text-xs text-center text-primary dark:text-secondary font-medium">
                  +{dayBookings.length - 3} more
                </div>
              )}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronLeft className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          </button>
          <h3 className="text-xl font-medium text-gray-900 dark:text-white">
            {currentDate.toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </h3>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronRight className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-px">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="p-2 text-center font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800"
            >
              {day}
            </div>
          ))}
          {days}
        </div>
      </div>
    );
  };

  // Render table view
  const renderTableView = () => {
    if (displayedBookings.length === 0) {
      return (
        <div className="text-center py-12 text-gray-600 dark:text-gray-400">
          No bookings found matching your current filters.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th
                scope="col"
                className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                onClick={() => handleSort("name")}
              >
                Name
                {sortColumn === "name" && (
                  <span className="ml-1">
                    {sortDirection === "asc" ? (
                      <ArrowUp className="h-3 w-3 inline" />
                    ) : (
                      <ArrowDown className="h-3 w-3 inline" />
                    )}
                  </span>
                )}
              </th>
              <th
                scope="col"
                className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden sm:table-cell"
                onClick={() => handleSort("email")}
              >
                Contact
                {sortColumn === "email" && (
                  <span className="ml-1">
                    {sortDirection === "asc" ? (
                      <ArrowUp className="h-3 w-3 inline" />
                    ) : (
                      <ArrowDown className="h-3 w-3 inline" />
                    )}
                  </span>
                )}
              </th>
              <th
                scope="col"
                className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                onClick={() => handleSort("date")}
              >
                Date & Time
                {sortColumn === "date" && (
                  <span className="ml-1">
                    {sortDirection === "asc" ? (
                      <ArrowUp className="h-3 w-3 inline" />
                    ) : (
                      <ArrowDown className="h-3 w-3 inline" />
                    )}
                  </span>
                )}
              </th>
              <th
                scope="col"
                className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell"
                onClick={() => handleSort("guests")}
              >
                Guests
                {sortColumn === "guests" && (
                  <span className="ml-1">
                    {sortDirection === "asc" ? (
                      <ArrowUp className="h-3 w-3 inline" />
                    ) : (
                      <ArrowDown className="h-3 w-3 inline" />
                    )}
                  </span>
                )}
              </th>
              <th
                scope="col"
                className="px-4 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {displayedBookings.map((booking) => (
              <motion.tr
                key={booking.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {booking.name}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 sm:hidden text-xs">
                    {booking.email && booking.email.length > 15
                      ? `${booking.email.substring(0, 15)}...`
                      : booking.email}
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                  <div className="text-gray-700 dark:text-gray-300">
                    {booking.email}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">
                    {booking.phone || "No phone"}
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                  <div className="text-gray-700 dark:text-gray-300">
                    {booking.date}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">
                    {booking.time}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 md:hidden text-xs">
                    {booking.guests} guests
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-gray-700 dark:text-gray-300 hidden md:table-cell">
                  {booking.guests}
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-center">
                  <button
                    onClick={() => setSelectedBooking(booking)}
                    className="text-primary dark:text-secondary hover:underline"
                  >
                    Details
                  </button>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Add force refresh function that clears localStorage cache before refreshing
  const handleForceRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setError(null);

    try {
      // First clear any cached data in localStorage
      localStorage.removeItem("bookingsCache");

      console.log("Force refreshing bookings data...");
      const data = await getBookings();

      if (!data || !Array.isArray(data)) {
        throw new Error("Invalid data returned from server");
      }

      console.log(
        `Successfully fetched ${data.length} bookings with force refresh`
      );

      // Update state with new data
      setBookings(data);

      // Filter and sort bookings
      const filteredData = filterAndSortBookings(data);
      setFilteredBookings(filteredData);

      toast.success(
        `Successfully loaded ${filteredData.length} upcoming bookings`
      );
    } catch (err) {
      console.error("Error force refreshing bookings:", err);
      setError(
        err instanceof Error ? err.message : "Failed to refresh bookings"
      );
      toast.error(
        err instanceof Error ? err.message : "Failed to refresh bookings"
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  // Functions to filter and search bookings
  const applyFilters = () => {
    let result = [...bookings] as EnhancedBooking[];

    // Apply date filter
    if (dateFilter === "upcoming") {
      result = result.filter((booking) => !isDateInPast(booking.date));
    } else if (dateFilter === "past") {
      result = result.filter((booking) => isDateInPast(booking.date));
    } else if (dateFilter === "today") {
      const today = new Date().toISOString().split("T")[0];
      result = result.filter((booking) => booking.date === today);
    } else if (dateFilter === "week") {
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);

      result = result.filter((booking) => {
        const bookingDate = new Date(booking.date);
        return bookingDate >= today && bookingDate <= nextWeek;
      });
    }
    // No filter for "all" option - show everything

    // Apply search
    if (searchTerm.trim() !== "") {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (booking) =>
          booking.name.toLowerCase().includes(search) ||
          booking.email.toLowerCase().includes(search) ||
          booking.phone?.toLowerCase().includes(search) ||
          booking.date.includes(search) ||
          booking.time.includes(search) ||
          booking.specialRequests?.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    if (sortColumn) {
      result.sort((a, b) => {
        let valueA: string | number | Date;
        let valueB: string | number | Date;

        // Handle different column types for sorting
        if (sortColumn === "date") {
          valueA = new Date(a.date);
          valueB = new Date(b.date);
        } else if (sortColumn === "time") {
          valueA = a.time;
          valueB = b.time;
        } else if (sortColumn === "guests") {
          valueA = parseInt(a.guests);
          valueB = parseInt(b.guests);
        } else {
          // Default string comparison for name, email
          valueA = a[sortColumn] || "";
          valueB = b[sortColumn] || "";
        }

        // Sort in selected direction
        if (sortDirection === "asc") {
          return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
        } else {
          return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
        }
      });
    } else {
      // Default sort by date and time
      result.sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time}`);
        const dateB = new Date(`${b.date}T${b.time}`);
        return dateA.getTime() - dateB.getTime();
      });
    }

    setDisplayedBookings(result);
  };

  // Apply filters when any filter changes
  useEffect(() => {
    if (bookings.length > 0) {
      applyFilters();
    }
  }, [searchTerm, dateFilter, sortColumn, sortDirection, bookings]);

  // Handle column sorting
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column and reset direction to asc
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Add notes to booking
  const addNotesToBooking = (bookingId: string, notes: string) => {
    setIsSavingNotes(true);

    // Simulate saving to database (would be an API call in production)
    setTimeout(() => {
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === bookingId ? { ...booking, notes } : booking
        )
      );

      // Save to localStorage as a backup
      try {
        const notesData = JSON.parse(
          localStorage.getItem("bookingNotes") || "{}"
        );
        notesData[bookingId] = notes;
        localStorage.setItem("bookingNotes", JSON.stringify(notesData));
      } catch (e) {
        console.error("Error saving notes to localStorage", e);
      }

      setIsSavingNotes(false);
      toast.success("Notes saved successfully");
    }, 500);
  };

  // Add new booking handler
  const handleAddBooking = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simple validation
    const errors: { [key: string]: string } = {};
    if (!newBooking.name.trim()) errors.name = "Name is required";
    if (!newBooking.email.trim()) errors.email = "Email is required";
    if (!newBooking.email.includes("@"))
      errors.email = "Valid email is required";
    if (!newBooking.date) errors.date = "Date is required";
    if (!newBooking.time) errors.time = "Time is required";
    if (!newBooking.guests) errors.guests = "Number of guests is required";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setIsLoading(true);
      const savedBooking = await addBooking(newBooking);

      // Add the new booking to the state
      setBookings((prev) => [...prev, savedBooking]);

      // Close the modal and reset form
      setShowAddBookingModal(false);
      setNewBooking({
        name: "",
        email: "",
        phone: "",
        date: new Date().toISOString().split("T")[0],
        time: "18:00",
        guests: "2",
        specialRequests: "",
        newsletter: false,
      });
      setFormErrors({});

      toast.success("Booking added successfully");
    } catch (error) {
      console.error("Error adding booking:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to add booking"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Export bookings to CSV
  const exportToCSV = () => {
    // Create CSV content
    const headers = [
      "Name",
      "Email",
      "Phone",
      "Date",
      "Time",
      "Guests",
      "Special Requests",
      "Created At",
    ];
    const csvRows = [headers.join(",")];

    displayedBookings.forEach((booking) => {
      const row = [
        `"${booking.name.replace(/"/g, '""')}"`,
        `"${booking.email.replace(/"/g, '""')}"`,
        `"${booking.phone?.replace(/"/g, '""') || ""}"`,
        booking.date,
        booking.time,
        booking.guests,
        `"${booking.specialRequests?.replace(/"/g, '""') || ""}"`,
        booking.createdAt,
      ];
      csvRows.push(row.join(","));
    });

    // Create and download file
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `bookings-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Bookings exported successfully");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <ToastContainer position="top-right" theme="colored" />

      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white text-center sm:text-left">
            Wave of Flavors - Admin
          </h1>
          <button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md transition-colors w-full sm:w-auto"
          >
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Upcoming Bookings{" "}
              {displayedBookings.length > 0
                ? `(${displayedBookings.length})`
                : ""}
              {bookings.length > displayedBookings.length && (
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                  {bookings.length - displayedBookings.length} bookings hidden
                  by filters
                </span>
              )}
            </h2>

            <div className="flex flex-wrap gap-2 justify-center sm:justify-end w-full sm:w-auto">
              {/* Add New Booking Button */}
              <button
                onClick={() => setShowAddBookingModal(true)}
                className="flex items-center px-3 py-2 rounded-md bg-green-500 text-white hover:bg-green-600 transition"
              >
                <Plus className="h-4 w-4 mr-1" />
                <span>Add Booking</span>
              </button>

              {/* Export CSV Button */}
              <button
                onClick={exportToCSV}
                className="flex items-center px-3 py-2 rounded-md bg-indigo-500 text-white hover:bg-indigo-600 transition"
                disabled={displayedBookings.length === 0}
              >
                <Download className="h-4 w-4 mr-1" />
                <span>Export CSV</span>
              </button>

              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`flex items-center px-3 py-2 rounded-md transition text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 ${isRefreshing ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-1 ${isRefreshing ? "animate-spin" : ""}`}
                />
                <span>Refresh</span>
              </button>

              <button
                onClick={handleForceRefresh}
                disabled={isRefreshing}
                className={`flex items-center px-3 py-2 rounded-md bg-blue-500 text-white hover:bg-blue-600 transition ${isRefreshing ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-1 ${isRefreshing ? "animate-spin" : ""}`}
                />
                <span>Force Refresh</span>
              </button>

              <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("table")}
                  className={`flex items-center px-3 py-2 rounded-md transition ${
                    viewMode === "table"
                      ? "bg-white dark:bg-gray-600 shadow-sm text-primary dark:text-secondary"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  <List className="h-4 w-4 mr-1" />
                  <span>Table</span>
                </button>
                <button
                  onClick={() => setViewMode("calendar")}
                  className={`flex items-center px-3 py-2 rounded-md transition ${
                    viewMode === "calendar"
                      ? "bg-white dark:bg-gray-600 shadow-sm text-primary dark:text-secondary"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>Calendar</span>
                </button>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search bookings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-4 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                <Filter className="h-4 w-4 mr-1" />
                <span>Filters</span>
              </button>
            </div>

            {/* Filter options */}
            {showFilters && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date Filter
                  </label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100"
                  >
                    <option value="all">All Dates</option>
                    <option value="upcoming">Upcoming Only</option>
                    <option value="past">Past Only</option>
                    <option value="today">Today</option>
                    <option value="week">Next 7 Days</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {isLoading && (
            <div className="flex justify-center py-12">
              <svg
                className="animate-spin h-8 w-8 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}

          {error && !isLoading && (
            <div className="bg-red-100 text-red-700 p-4 rounded-md mb-6">
              {error}
            </div>
          )}

          {!isLoading && !error && bookings.length === 0 && (
            <div className="text-center py-12 text-gray-600 dark:text-gray-400">
              No bookings found.
            </div>
          )}

          {!isLoading && bookings.length > 0 && (
            <>
              {viewMode === "table" ? renderTableView() : renderCalendarView()}
            </>
          )}
        </div>
      </main>

      {/* Booking Detail Modal */}
      <AnimatePresence>
        {selectedBooking && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Booking Details
                  </h3>
                  <button
                    onClick={() => setSelectedBooking(null)}
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Customer Info */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Customer Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <User className="h-5 w-5 mr-2 text-primary dark:text-secondary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Name
                          </div>
                          <div className="text-gray-900 dark:text-white">
                            {selectedBooking.name}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <Mail className="h-5 w-5 mr-2 text-primary dark:text-secondary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Email
                          </div>
                          <div className="text-gray-900 dark:text-white break-all">
                            {selectedBooking.email}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <Phone className="h-5 w-5 mr-2 text-primary dark:text-secondary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Phone
                          </div>
                          <div className="text-gray-900 dark:text-white">
                            {selectedBooking.phone || "None provided"}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <Users className="h-5 w-5 mr-2 text-primary dark:text-secondary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Party Size
                          </div>
                          <div className="text-gray-900 dark:text-white">
                            {selectedBooking.guests} guests
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Booking Info */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Booking Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <CalendarIcon className="h-5 w-5 mr-2 text-primary dark:text-secondary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Date
                          </div>
                          <div className="text-gray-900 dark:text-white">
                            {selectedBooking.date}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <Clock className="h-5 w-5 mr-2 text-primary dark:text-secondary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Time
                          </div>
                          <div className="text-gray-900 dark:text-white">
                            {selectedBooking.time}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <MessageSquare className="h-5 w-5 mr-2 text-primary dark:text-secondary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Special Requests
                          </div>
                          <div className="text-gray-900 dark:text-white">
                            {selectedBooking.specialRequests || "None"}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <Clock className="h-5 w-5 mr-2 text-primary dark:text-secondary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Booked On
                          </div>
                          <div className="text-gray-900 dark:text-white">
                            {selectedBooking.createdAt
                              ? formatDate(selectedBooking.createdAt)
                              : "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Admin Notes
                    </h4>
                    <textarea
                      value={selectedBooking.notes || ""}
                      onChange={(e) =>
                        setSelectedBooking({
                          ...selectedBooking,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Add private notes about this booking..."
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      rows={3}
                    />
                    <button
                      onClick={() => {
                        if (selectedBooking.id) {
                          addNotesToBooking(
                            selectedBooking.id,
                            selectedBooking.notes || ""
                          );
                        }
                      }}
                      disabled={isSavingNotes}
                      className="mt-2 bg-primary hover:bg-primary/90 text-white px-3 py-1 rounded-md text-sm flex items-center"
                    >
                      {isSavingNotes ? (
                        <>
                          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Notes"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Booking Modal */}
      <AnimatePresence>
        {showAddBookingModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Add New Booking
                  </h3>
                  <button
                    onClick={() => setShowAddBookingModal(false)}
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleAddBooking} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Name */}
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={newBooking.name}
                        onChange={(e) =>
                          setNewBooking({ ...newBooking, name: e.target.value })
                        }
                        className={`w-full rounded-md border ${formErrors.name ? "border-red-500" : "border-gray-300 dark:border-gray-600"} bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary`}
                      />
                      {formErrors.name && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors.name}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={newBooking.email}
                        onChange={(e) =>
                          setNewBooking({
                            ...newBooking,
                            email: e.target.value,
                          })
                        }
                        className={`w-full rounded-md border ${formErrors.email ? "border-red-500" : "border-gray-300 dark:border-gray-600"} bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary`}
                      />
                      {formErrors.email && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors.email}
                        </p>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={newBooking.phone}
                        onChange={(e) =>
                          setNewBooking({
                            ...newBooking,
                            phone: e.target.value,
                          })
                        }
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>

                    {/* Guests */}
                    <div>
                      <label
                        htmlFor="guests"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Number of Guests *
                      </label>
                      <select
                        id="guests"
                        value={newBooking.guests}
                        onChange={(e) =>
                          setNewBooking({
                            ...newBooking,
                            guests: e.target.value,
                          })
                        }
                        className={`w-full rounded-md border ${formErrors.guests ? "border-red-500" : "border-gray-300 dark:border-gray-600"} bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary`}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                          <option key={num} value={num.toString()}>
                            {num}
                          </option>
                        ))}
                      </select>
                      {formErrors.guests && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors.guests}
                        </p>
                      )}
                    </div>

                    {/* Date */}
                    <div>
                      <label
                        htmlFor="date"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Date *
                      </label>
                      <input
                        type="date"
                        id="date"
                        value={newBooking.date}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={(e) =>
                          setNewBooking({ ...newBooking, date: e.target.value })
                        }
                        className={`w-full rounded-md border ${formErrors.date ? "border-red-500" : "border-gray-300 dark:border-gray-600"} bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary`}
                      />
                      {formErrors.date && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors.date}
                        </p>
                      )}
                    </div>

                    {/* Time */}
                    <div>
                      <label
                        htmlFor="time"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Time *
                      </label>
                      <select
                        id="time"
                        value={newBooking.time}
                        onChange={(e) =>
                          setNewBooking({ ...newBooking, time: e.target.value })
                        }
                        className={`w-full rounded-md border ${formErrors.time ? "border-red-500" : "border-gray-300 dark:border-gray-600"} bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary`}
                      >
                        {[
                          "16:00",
                          "16:30",
                          "17:00",
                          "17:30",
                          "18:00",
                          "18:30",
                          "19:00",
                          "19:30",
                          "20:00",
                          "20:30",
                          "21:00",
                        ].map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                      {formErrors.time && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors.time}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Special Requests */}
                  <div>
                    <label
                      htmlFor="specialRequests"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Special Requests
                    </label>
                    <textarea
                      id="specialRequests"
                      value={newBooking.specialRequests}
                      onChange={(e) =>
                        setNewBooking({
                          ...newBooking,
                          specialRequests: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>

                  {/* Newsletter */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="newsletter"
                      checked={newBooking.newsletter}
                      onChange={(e) =>
                        setNewBooking({
                          ...newBooking,
                          newsletter: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label
                      htmlFor="newsletter"
                      className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                    >
                      Subscribe to newsletter
                    </label>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowAddBookingModal(false)}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-md transition flex items-center"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-1" />
                          Add Booking
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
